import { useQuery } from '@tanstack/react-query'
import { useClientAuth } from '../../contexts/ClientAuthContext'
import { clientService } from '../../services/clientService'
import { Briefcase, FileText, Clock, Bell } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'

const ClientDashboard = () => {
  const { client } = useClientAuth()

  // Fetch stats
  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['clientStats'],
    queryFn: () => clientService.getStats(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  // Fetch recent cases (limit 5)
  const { data: casesData, isLoading: casesLoading } = useQuery({
    queryKey: ['clientCases', 'recent'],
    queryFn: () => clientService.getCases({ limit: 5, offset: 0 }),
    staleTime: 2 * 60 * 1000,
  })

  // Fetch recent documents (limit 5)
  const { data: documentsData, isLoading: documentsLoading } = useQuery({
    queryKey: ['clientDocuments', 'recent'],
    queryFn: () => clientService.getDocuments({ limit: 5, offset: 0 }),
    staleTime: 2 * 60 * 1000,
  })

  // Compute stats for dashboard cards
  const stats = [
    {
      label: 'Active Cases',
      value: statsData?.active_cases ?? 0,
      icon: Briefcase,
      color: 'bg-blue-500',
      loading: statsLoading,
    },
    {
      label: 'Shared Documents',
      value: statsData?.shared_documents ?? 0,
      icon: FileText,
      color: 'bg-green-500',
      loading: statsLoading,
    },
    {
      label: 'Upcoming Deadlines',
      value: statsData?.upcoming_deadlines ?? 0,
      icon: Clock,
      color: 'bg-amber-500',
      loading: statsLoading,
    },
    {
      label: 'Unread Notifications',
      value: statsData?.unread_notifications ?? 0,
      icon: Bell,
      color: 'bg-purple-500',
      loading: statsLoading,
    },
  ]

  const recentCases = casesData?.cases?.slice(0, 5) || []
  const recentDocuments = documentsData?.documents?.slice(0, 5) || []

  const formatUploadTime = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true })
    } catch {
      return dateString
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const handleDownload = async (documentId: number, filename: string) => {
    try {
      const url = await clientService.downloadDocument(documentId)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Download failed:', error)
      alert('Failed to download document')
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Welcome back, {client?.first_name}!</h1>
        <p className="text-gray-600">Here's what's happening with your cases.</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => {
          const Icon = stat.icon
          return (
            <div key={stat.label} className="card p-4">
              <div className="flex items-center">
                <div className={`${stat.color} p-3 rounded-lg`}>
                  <Icon className="h-6 w-6 text-white" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">{stat.label}</p>
                  {stat.loading ? (
                    <div className="h-8 w-12 animate-pulse bg-gray-200 rounded"></div>
                  ) : (
                    <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Recent Cases */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Recent Cases</h2>
          <a href="/client/cases" className="text-sm text-blue-600 hover:underline">
            View all
          </a>
        </div>
        {casesLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : recentCases.length === 0 ? (
          <p className="text-gray-500 text-center py-4">No cases found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Case Number
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Priority
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {recentCases.map((c) => (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                      {c.case_number}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">{c.title}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          c.status === 'Completed'
                            ? 'bg-green-100 text-green-800'
                            : c.status === 'In Progress'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {c.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          c.priority === 'High'
                            ? 'bg-red-100 text-red-800'
                            : c.priority === 'Medium'
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {c.priority}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recent Documents & Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Documents */}
        <div className="card lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Recent Shared Documents</h2>
            <a href="/client/documents" className="text-sm text-blue-600 hover:underline">
              View all
            </a>
          </div>
          {documentsLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                  <div className="flex items-center">
                    <div className="h-5 w-5 bg-gray-300 rounded mr-3"></div>
                    <div>
                      <div className="h-4 w-48 bg-gray-300 rounded mb-2"></div>
                      <div className="h-3 w-32 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                  <div className="h-4 w-16 bg-gray-300 rounded"></div>
                </div>
              ))}
            </div>
          ) : recentDocuments.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No shared documents.</p>
          ) : (
            <div className="space-y-4">
              {recentDocuments.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                  <div className="flex items-center">
                    <FileText className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{doc.filename}</p>
                      <p className="text-xs text-gray-500">
                        Uploaded {formatUploadTime(doc.uploaded_at)} • {formatFileSize(doc.file_size)}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDownload(doc.id, doc.filename)}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Download
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="space-y-3">
            <button
              onClick={() => window.location.href = '/client/documents?upload=true'}
              className="w-full flex items-center justify-center py-2.5 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              <FileText className="h-5 w-5 mr-2" />
              Upload Document
            </button>
            <button
              onClick={() => window.location.href = '/client/notifications'}
              className="w-full flex items-center justify-center py-2.5 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              <Bell className="h-5 w-5 mr-2" />
              View Notifications
            </button>
            <button
              onClick={() => window.location.href = '/client/deadlines'}
              className="w-full flex items-center justify-center py-2.5 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              <Clock className="h-5 w-5 mr-2" />
              Check Deadlines
            </button>
            <button
              onClick={() => window.location.href = '/client/profile?tab=support'}
              className="w-full flex items-center justify-center py-2.5 px-4 border border-blue-300 rounded-lg text-blue-700 hover:bg-blue-50"
            >
              Contact Support
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ClientDashboard